import React, { useState, useCallback, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ReplyIcon from "@material-ui/icons/Reply";
import LaunchIcon from "@material-ui/icons/Launch";
import CallIcon from "@material-ui/icons/Call";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const MENU_FONT = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const useStyles = makeStyles(theme => ({
  paper: { width: 400, maxWidth: "calc(100% - 24px)", borderRadius: 14, fontFamily: MENU_FONT, overflow: "hidden" },
  empty: { padding: theme.spacing(4, 2), textAlign: "center", color: theme.app.textTertiary, fontSize: 13 },
  head: { display: "flex", alignItems: "center", gap: 9, padding: "10px 12px", background: "#FBF6E9", borderBottom: `1px solid ${theme.app.border}` },
  headIcon: { width: 22, height: 22, borderRadius: 6, background: "#25D366", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 15 } },
  headTitle: { flex: 1, fontSize: 13, fontWeight: 600, color: theme.app.ink },
  counter: { fontSize: 11, fontFamily: theme.app.mono, color: theme.app.textTertiary },
  iconBtn: { color: theme.app.textSecondary, cursor: "pointer", padding: 4, display: "flex", borderRadius: 6, "&:hover": { color: theme.app.ink, background: theme.app.hover } },
  body: { padding: "10px 12px 12px" },
  metaRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 2 },
  name: { flex: 1, fontSize: 12.5, fontWeight: 600, color: theme.app.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  badge: { fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 11, color: "#fff", flexShrink: 0, letterSpacing: ".02em" },
  sub: { fontSize: 11, color: theme.app.textTertiary, marginBottom: 9 },
  reason: { fontSize: 10.5, color: "#B12A2A", margin: "6px 0 0" },

  // edição de conteúdo
  editLabel: { fontSize: 11, fontWeight: 600, color: theme.app.textSecondary, margin: "8px 0 4px" },
  editArea: { width: "100%", boxSizing: "border-box", border: `1px solid ${theme.app.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: MENU_FONT, lineHeight: 1.4, outline: "none", resize: "vertical", "&:focus": { borderColor: "#2C353D" } },
  varRow: { display: "flex", alignItems: "center", gap: 8, margin: "6px 0" },
  varBtn: { border: `1px solid ${theme.app.border}`, background: "#fff", color: "#2C353D", borderRadius: 7, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: MENU_FONT, "&:hover": { background: theme.app.hover } },
  varHint: { fontSize: 10.5, color: theme.app.textTertiary },
  exInput: { width: "100%", boxSizing: "border-box", border: `1px solid ${theme.app.border}`, borderRadius: 7, padding: "6px 9px", fontSize: 12.5, fontFamily: MENU_FONT, outline: "none", marginBottom: 5, "&:focus": { borderColor: "#2C353D" } },
  linkBtn: { border: "none", background: "none", color: theme.app.info, fontSize: 12.5, fontWeight: 600, cursor: "pointer", padding: "6px 4px", fontFamily: MENU_FONT },

  // preview estilo WhatsApp
  chat: { background: "#E4DDD3", backgroundImage: "radial-gradient(rgba(0,0,0,.04) 1px, transparent 1px)", backgroundSize: "14px 14px", borderRadius: 10, padding: 12, display: "flex" },
  bubble: { background: "#fff", borderRadius: "0 8px 8px 8px", boxShadow: "0 1px 1px rgba(0,0,0,.13)", maxWidth: "92%", overflow: "hidden", position: "relative" },
  bubbleInner: { padding: "7px 9px 5px" },
  hdr: { fontSize: 13.5, fontWeight: 700, color: "#111b21", marginBottom: 3, lineHeight: 1.3, wordBreak: "break-word" },
  text: { fontSize: 13.5, color: "#111b21", lineHeight: 1.34, whiteSpace: "pre-wrap", wordBreak: "break-word" },
  ftr: { fontSize: 11.5, color: "#8696a0", marginTop: 5, lineHeight: 1.3, wordBreak: "break-word" },
  time: { fontSize: 10, color: "#8696a0", textAlign: "right", marginTop: 2, lineHeight: 1 },
  btns: { borderTop: "1px solid #e9edef" },
  btn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 6px", fontSize: 13, fontWeight: 500, color: "#1ea860", borderTop: "1px solid #f0f2f5", cursor: "default", "&:first-child": { borderTop: "none" }, "& svg": { fontSize: 16 } },

  // ações / navegação do carrossel
  actions: { display: "flex", alignItems: "center", gap: 8, marginTop: 11 },
  nav: { color: theme.app.textSecondary, cursor: "pointer", padding: 2, display: "flex", borderRadius: 6, "&:hover": { color: theme.app.ink, background: theme.app.hover }, "&.dis": { opacity: 0.3, pointerEvents: "none" } },
  dots: { display: "flex", gap: 5, alignItems: "center" },
  dot: { width: 6, height: 6, borderRadius: "50%", background: theme.app.border, cursor: "pointer" },
  dotOn: { background: "#2C353D" },
  grow: { flex: 1 },
  createBtn: { border: "none", background: "#2C353D", color: "#fff", borderRadius: 8, padding: "7px 16px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: MENU_FONT, "&:disabled": { background: theme.app.border, color: theme.app.textTertiary, cursor: "default" } },
  foot: { fontSize: 10.5, color: theme.app.textTertiary, padding: "0 12px 10px", lineHeight: 1.4 },
}));

const STATUS = {
  DRAFT: { label: "RASCUNHO", color: "#777" },
  PENDING: { label: "AGUARDANDO APROVAÇÃO", color: "#B26A00" },
  APPROVED: { label: "APROVADO", color: "#1E7E34" },
  REJECTED: { label: "REJEITADO", color: "#B12A2A" },
  FAILED: { label: "FALHOU", color: "#B12A2A" },
};

// substitui {{1}},{{2}}… pelos valores de amostra (na ordem); fallback "exemplo"
const fillExample = (text, samples = []) =>
  String(text || "").replace(/\{\{\s*(\d+)\s*\}\}/g, (_, d) => samples[Number(d) - 1] || "exemplo");

// regras da Meta (espelham GupshupTemplates.validate) — evita um FAILED garantido
const validate = d => {
  const c = (d.content || "").trim();
  if (!d.elementName || !c) return "Template sem nome ou conteúdo.";
  if (/^\{\{\s*\d+\s*\}\}/.test(c)) return `"${d.elementName}": o conteúdo não pode COMEÇAR com variável.`;
  if (/\{\{\s*\d+\s*\}\}$/.test(c)) return `"${d.elementName}": o conteúdo não pode TERMINAR com variável.`;
  const nums = (c.match(/\{\{\s*(\d+)\s*\}\}/g) || []).map(s => parseInt(s.replace(/\D/g, ""), 10));
  const uniq = [...new Set(nums)].sort((a, b) => a - b);
  if (uniq.some((n, i) => n !== i + 1)) return `"${d.elementName}": variáveis devem ser {{1}},{{2}}… em ordem, sem pular.`;
  return null;
};

const btnIcon = type => {
  if (type === "URL") return <LaunchIcon />;
  if (type === "PHONE_NUMBER") return <CallIcon />;
  return <ReplyIcon />; // QUICK_REPLY
};

// Pré-visualização fiel do template como o WhatsApp/Meta renderiza.
const WhatsappPreview = ({ classes, d, time }) => {
  const header = fillExample(d.header, d.headerExample ? [d.headerExample] : []);
  const body = fillExample(d.content, d.examples);
  const buttons = Array.isArray(d.buttons) ? d.buttons : [];
  return (
    <div className={classes.chat}>
      <div className={classes.bubble}>
        <div className={classes.bubbleInner}>
          {header ? <div className={classes.hdr}>{header}</div> : null}
          <div className={classes.text}>{body}</div>
          {d.footer ? <div className={classes.ftr}>{d.footer}</div> : null}
          <div className={classes.time}>{time}</div>
        </div>
        {buttons.length > 0 && (
          <div className={classes.btns}>
            {buttons.map((b, i) => (
              <div key={i} className={classes.btn}>
                {btnIcon(b.type)} {b.text}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Card de pendência: carrossel dos nós "Enviar template" com um templateDraft
// ainda não aprovado, mostrando a pré-visualização real do WhatsApp.
const WhatsappTemplatePending = ({ open, onClose, focusNodeId, elements, onPatchNode, isSessionOwned, onTemplateCreated }) => {
  const classes = useStyles();
  const [busy, setBusy] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [idx, setIdx] = useState(0);
  const [edit, setEdit] = useState(null); // edição do conteúdo: {content, examples[], footer}
  const [time] = useState(() =>
    new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
  );

  const drafts = (elements || [])
    .filter(el => el && el.data && el.data.templateDraft && el.data.templateDraft.status !== "APPROVED")
    .map(el => ({ nodeId: el.id, connectorId: el.data.connectorId, d: el.data.templateDraft }));

  useEffect(() => {
    if (idx > drafts.length - 1) setIdx(Math.max(0, drafts.length - 1));
  }, [drafts.length, idx]);

  // ao trocar de template no carrossel, sai do modo edição
  useEffect(() => {
    setEdit(null);
  }, [idx]);

  // nº de variáveis {{n}} no texto
  const varCount = txt => {
    const ns = (String(txt || "").match(/\{\{\s*(\d+)\s*\}\}/g) || []).map(s => parseInt(s.replace(/\D/g, ""), 10));
    return ns.length ? Math.max(...ns) : 0;
  };

  // ao abrir pelo badge de um nó, foca o template daquele nó
  useEffect(() => {
    if (!open || !focusNodeId) return;
    const k = drafts.findIndex(x => x.nodeId === focusNodeId);
    if (k >= 0) setIdx(k);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, focusNodeId]);

  const create = useCallback(
    async ({ nodeId, connectorId, d }) => {
      const problem = validate(d);
      if (problem) { toast.warn(problem); return false; }
      setBusy(b => ({ ...b, [d.elementName]: true }));
      try {
        const headerHasVar = /\{\{\s*\d+\s*\}\}/.test(d.header || "");
        const replace = isSessionOwned ? isSessionOwned(d.elementName) : false;
        const { data } = await api.post("/connectors/gupshup/templates", {
          elementName: d.elementName,
          category: d.category,
          languageCode: d.languageCode,
          content: d.content,
          header: d.header || undefined,
          exampleHeader: headerHasVar ? d.headerExample || "exemplo" : undefined,
          footer: d.footer || undefined,
          buttons: Array.isArray(d.buttons) && d.buttons.length ? d.buttons : undefined,
          example: fillExample(d.content, d.examples),
          replace,
          connectorId: connectorId || undefined,
        });
        const finalName = (data && data.elementName) || d.elementName;
        if (onTemplateCreated) onTemplateCreated(finalName);
        onPatchNode(nodeId, {
          templateDraft: { ...d, elementName: finalName, status: "PENDING" },
          templateName: finalName,
        });
        if (data && data.renamed) toast.info(`Já existia "${d.elementName}" — criei "${finalName}".`);
        else if (data && data.replaced) toast.success(`Template "${finalName}" atualizado e reenviado para aprovação.`);
        else toast.success(`Template "${finalName}" enviado para aprovação.`);
        return true;
      } catch (err) {
        toastError(err);
        return false;
      } finally {
        setBusy(b => ({ ...b, [d.elementName]: false }));
      }
    },
    [onPatchNode, isSessionOwned, onTemplateCreated]
  );

  // após criar com sucesso, avança para o próximo template que ainda é rascunho
  const advanceFrom = useCallback(
    fromIdx => {
      const next = drafts.findIndex((x, k) => k !== fromIdx && x.d.status === "DRAFT");
      if (next >= 0) setIdx(next);
    },
    [drafts]
  );

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const connIds = [...new Set(drafts.map(x => x.connectorId).filter(Boolean))];
      const byName = {};
      const lists = await Promise.all(
        (connIds.length ? connIds : [null]).map(cid =>
          api
            .get("/connectors/gupshup/templates", { params: cid ? { connectorId: cid } : {} })
            .then(r => r.data.templates || [])
            .catch(() => [])
        )
      );
      lists.flat().forEach(t => { byName[t.elementName] = t; });
      let changed = 0;
      drafts.forEach(({ nodeId, d }) => {
        const t = byName[d.elementName];
        if (!t || !t.status) return;
        const status = String(t.status).toUpperCase();
        if (status !== d.status) {
          changed += 1;
          const patch = { templateDraft: { ...d, status, reason: t.reason || "" } };
          if (status === "APPROVED" && (t.id || t.elementName)) patch.templateId = t.id || t.elementName;
          onPatchNode(nodeId, patch);
        }
      });
      if (!changed) toast.info("Sem mudança de status ainda.");
    } finally {
      setRefreshing(false);
    }
  }, [drafts, onPatchNode]);

  if (!open) return null;

  if (!drafts.length) {
    return (
      <Dialog open onClose={onClose} maxWidth="xs" classes={{ paper: classes.paper }}>
        <div className={classes.head}>
          <span className={classes.headIcon}><WhatsAppIcon /></span>
          <span className={classes.headTitle}>Templates WhatsApp</span>
          <IconButton size="small" className={classes.iconBtn} onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </div>
        <div className={classes.empty}>Nenhum template pendente.</div>
      </Dialog>
    );
  }

  const i = Math.min(idx, drafts.length - 1);
  const cur = drafts[i];
  const st = STATUS[cur.d.status] || STATUS.DRAFT;
  const many = drafts.length > 1;

  return (
    <Dialog open onClose={onClose} maxWidth="xs" classes={{ paper: classes.paper }}>
      <div className={classes.head}>
        <span className={classes.headIcon}><WhatsAppIcon /></span>
        <span className={classes.headTitle}>Criar templates WhatsApp</span>
        {many && <span className={classes.counter}>{i + 1} / {drafts.length}</span>}
        <span className={classes.iconBtn} title="Atualizar status" onClick={refreshing ? undefined : refresh}>
          {refreshing ? <CircularProgress size={15} /> : <RefreshIcon fontSize="small" />}
        </span>
        <IconButton size="small" className={classes.iconBtn} onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </div>

      <div className={classes.body}>
        <div className={classes.metaRow}>
          <span className={classes.name}>{cur.d.elementName}</span>
          <span className={classes.badge} style={{ background: st.color }}>{st.label}</span>
        </div>
        <div className={classes.sub}>{cur.d.category} · {cur.d.languageCode}</div>

        {edit ? (
          (() => {
            const nVars = varCount(edit.content);
            return (
              <div>
                <div className={classes.editLabel}>Mensagem</div>
                <textarea
                  className={classes.editArea}
                  rows={4}
                  value={edit.content}
                  onChange={e => setEdit({ ...edit, content: e.target.value })}
                />
                <div className={classes.varRow}>
                  <button
                    type="button"
                    className={classes.varBtn}
                    onClick={() => setEdit({ ...edit, content: `${edit.content}{{${varCount(edit.content) + 1}}}` })}
                  >
                    + variável
                  </button>
                  <span className={classes.varHint}>{"insere {{1}}, {{2}}… — não comece/termine o texto com variável"}</span>
                </div>
                {nVars > 0 && (
                  <>
                    <div className={classes.editLabel}>Exemplo de cada variável (a Meta revisa por eles)</div>
                    {Array.from({ length: nVars }).map((_, k) => (
                      <input
                        key={k}
                        className={classes.exInput}
                        placeholder={`Exemplo para {{${k + 1}}}`}
                        value={(edit.examples && edit.examples[k]) || ""}
                        onChange={e => {
                          const ex = [...(edit.examples || [])];
                          ex[k] = e.target.value;
                          setEdit({ ...edit, examples: ex });
                        }}
                      />
                    ))}
                  </>
                )}
                <div className={classes.editLabel}>Rodapé (opcional)</div>
                <input className={classes.exInput} value={edit.footer || ""} onChange={e => setEdit({ ...edit, footer: e.target.value })} />
              </div>
            );
          })()
        ) : (
          <>
            <WhatsappPreview classes={classes} d={cur.d} time={time} />
            {(cur.d.status === "REJECTED" || cur.d.status === "FAILED") && cur.d.reason && (
              <div className={classes.reason}>Motivo: {cur.d.reason}</div>
            )}
          </>
        )}

        <div className={classes.actions}>
          {edit ? (
            <>
              <button type="button" className={classes.linkBtn} onClick={() => setEdit(null)}>Cancelar</button>
              <span className={classes.grow} />
              <button
                type="button"
                className={classes.createBtn}
                onClick={() => {
                  onPatchNode(cur.nodeId, {
                    templateDraft: { ...cur.d, content: edit.content, examples: edit.examples || [], footer: edit.footer || "" },
                    templateBody: edit.content,
                  });
                  setEdit(null);
                }}
              >
                Salvar
              </button>
            </>
          ) : (
            <>
              {many && (
                <>
                  <span className={`${classes.nav} ${i === 0 ? "dis" : ""}`} onClick={() => setIdx(v => Math.max(0, v - 1))}>
                    <ChevronLeftIcon fontSize="small" />
                  </span>
                  <span className={classes.dots}>
                    {drafts.map((x, k) => (
                      <span key={x.nodeId} className={`${classes.dot} ${k === i ? classes.dotOn : ""}`} onClick={() => setIdx(k)} />
                    ))}
                  </span>
                  <span className={`${classes.nav} ${i === drafts.length - 1 ? "dis" : ""}`} onClick={() => setIdx(v => Math.min(drafts.length - 1, v + 1))}>
                    <ChevronRightIcon fontSize="small" />
                  </span>
                </>
              )}
              <span className={classes.grow} />
              {cur.d.status === "DRAFT" && (
                <>
                  <button type="button" className={classes.varBtn} style={{ marginRight: 8 }} onClick={() => setEdit({ content: cur.d.content || "", examples: [...(cur.d.examples || [])], footer: cur.d.footer || "" })}>
                    Editar conteúdo
                  </button>
                  <button
                    type="button"
                    className={classes.createBtn}
                    disabled={!!busy[cur.d.elementName]}
                    onClick={async () => {
                      const ok = await create(cur);
                      if (ok) advanceFrom(i);
                    }}
                  >
                    {busy[cur.d.elementName] ? "Criando…" : "Criar"}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className={classes.foot}>
        Ao criar, o template é enviado ao WhatsApp para aprovação (pode levar horas). Use ↻ para atualizar o status.
      </div>
    </Dialog>
  );
};

export default WhatsappTemplatePending;
